import { useState, useEffect } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useDeviceDetection } from './useDeviceDetection';

export interface AppContext {
  type: 'admin' | 'user' | 'public' | 'global';
  scope: string;
  route: string;
  title: string;
  permissions: string[];
  features: string[];
}

export interface ContextDetectionResult {
  currentContext: AppContext;
  previousContext: AppContext | null;
  contextChanged: boolean;
  isAdmin: boolean;
  isUser: boolean;
  isPublic: boolean;
  isGlobal: boolean;
}

/**
 * Context detection hook following established patterns
 * Integrates with existing device detection and follows modular architecture
 */
export const useContextDetection = (): ContextDetectionResult => {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { isMobile: _isMobile } = useDeviceDetection();
  const [previousContext, setPreviousContext] = useState<AppContext | null>(null);

  // Route-to-context mapping following existing patterns
  const getContextFromRoute = (pathname: string): AppContext => {
    // Admin routes (pathless _admin layout renders URLs without /admin prefix)
    if (pathname.startsWith('/dashboard') || pathname.startsWith('/admin') || pathname.startsWith('/cortex')) {
      if (pathname.startsWith('/admin/settings')) {
        return {
          type: 'admin',
          scope: 'admin_settings',
          route: pathname,
          title: 'Admin Settings',
          permissions: ['admin', 'settings'],
          features: ['system_config', 'integrations', 'security']
        };
      }
      if (pathname.startsWith('/admin/users') || pathname.startsWith('/users')) {
        return {
          type: 'admin',
          scope: 'admin_users',
          route: pathname,
          title: 'User Management',
          permissions: ['admin', 'users'],
          features: ['user_management', 'permissions', 'analytics']
        };
      }
      if (pathname.startsWith('/admin/analytics') || pathname.startsWith('/analytics')) {
        return {
          type: 'admin',
          scope: 'admin_analytics',
          route: pathname,
          title: 'Analytics Dashboard',
          permissions: ['admin', 'analytics'],
          features: ['reports', 'metrics', 'insights']
        };
      }
      return {
        type: 'admin',
        scope: 'admin_general',
        route: pathname,
        title: 'Admin Dashboard',
        permissions: ['admin'],
        features: ['dashboard', 'overview', 'management']
      };
    }

    // Personal/user routes
    if (pathname.startsWith('/profile')) {
      return {
        type: 'user',
        scope: 'user_profile',
        route: pathname,
        title: 'My Profile',
        permissions: ['user'],
        features: ['profile_management', 'preferences', 'settings']
      };
    }
    if (pathname.startsWith('/settings')) {
      return {
        type: 'user',
        scope: 'user_settings',
        route: pathname,
        title: 'Personal Settings',
        permissions: ['user'],
        features: ['personal_settings', 'notifications', 'theme', 'ai']
      };
    }
    if (pathname.startsWith('/tasks')) {
      return {
        type: 'user',
        scope: 'user_tasks',
        route: pathname,
        title: 'My Tasks',
        permissions: ['user'],
        features: ['task_management', 'productivity', 'calendar']
      };
    }

    // Public routes
    if (pathname === '/' || pathname.startsWith('/public')) {
      return {
        type: 'public',
        scope: 'public_general',
        route: pathname,
        title: 'Welcome',
        permissions: [],
        features: ['public_info', 'help', 'contact']
      };
    }

    // Global context for cross-cutting features
    return {
      type: 'global',
      scope: 'global_general',
      route: pathname,
      title: 'AI Assistant',
      permissions: ['global'],
      features: ['ai_assistant', 'help', 'search']
    };
  };

  const currentContext = getContextFromRoute(pathname);
  const contextChanged = previousContext?.scope !== currentContext.scope;

  useEffect(() => {
    if (contextChanged) {
      setPreviousContext(currentContext);
    }
  }, [contextChanged, currentContext]);

  return {
    currentContext,
    previousContext,
    contextChanged,
    isAdmin: currentContext.type === 'admin',
    isUser: currentContext.type === 'user',
    isPublic: currentContext.type === 'public',
    isGlobal: currentContext.type === 'global'
  };
};
